import { useState } from 'react';
import { coreValues, employeeBenefits } from '../data/siteData';
import Reveal from './Reveal';
import LeadershipHero from './LeadershipHero';
import './Leadership.css';

// A one-line summary shown under each card's heading while it's at rest —
// the equivalent of the reference cards' descriptive sub-line. The full
// copy (item.desc) still reveals on hover.
const valueHints = {
  Commitment: 'Fair benefit to every stakeholder, in every market condition.',
  Integrity: 'Trust, transparency, and accountability in every relationship.',
  'Free Education': "Free schooling for our employees' children.",
  'Medical Insurance': '100% of the premium covered for eligible employees.',
  'Pension Benefits': '100% company-funded, for every eligible employee.',
};

// Drop each value/benefit's photo into public/images/CoreValues/.
const valueImages = {
  Commitment: '/images/CoreValues/Commitment images 2.jpg',
  Integrity: '/images/CoreValues/Integarety honesty.jpg',
  'Free Education': '/images/CoreValues/Free Education.jpg',
  'Medical Insurance': '/images/CoreValues/Medical Insuranse.png',
  'Pension Benefits': '/images/CoreValues/Pension.jpg',
};

function Leadership() {
  const employeeItems = [
    ...coreValues.filter((v) => v.title === 'Commitment' || v.title === 'Integrity'),
    ...employeeBenefits,
  ];
  // No card is active on load — every image starts in its normal state,
  // and only the one being hovered/focused reveals its content.
  const [activeEmployeeIndex, setActiveEmployeeIndex] = useState(null);

  return (
    <section id="leadership" className="section leadership">
      {/* Hidden clip-path definition for the benefit card images — a
          pentagon with rounded corners, which plain CSS clip-path:
          polygon() can't produce (it only draws straight, sharp edges).
          The path below uses a uniform 14px radius at every corner —
          computed against the image's actual ~243x160px box, since a
          single fractional radius would round unevenly on a non-square
          box (the same 0-1 fraction is a different pixel amount on the
          x axis than on the y axis). */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <clipPath id="benefit-pentagon" clipPathUnits="objectBoundingBox">
            <path
              d="M0.0576,0 L0.6425,0 Q0.7,0 0.7388,0.0646 L0.9613,0.4354
                 Q1,0.5 0.9613,0.5646 L0.7388,0.9354 Q0.7,1 0.6425,1
                 L0.0576,1 Q0,1 0,0.9125 L0,0.0875 Q0,0 0.0576,0 Z"
            />
          </clipPath>
        </defs>
      </svg>

      <LeadershipHero />

      <div className="section-inner">
        <div className="speaks">
          <Reveal variant="up" className="speaks-heading">
            <span className="eyebrow" style={{ justifyContent: 'center' }}>
              In Their Words
            </span>
            <h2>
              Core <span className="accent">Values</span>
            </h2>
          </Reveal>

          <div className="values-grid">
            {coreValues
              .filter((v) => v.title === 'Our Mission' || v.title === 'Our Vision')
              .map((v, i) => (
                <Reveal variant="up" delay={i * 100} as="div" className="value-card" key={v.title}>
                  <h4>{v.title}</h4>
                  <p>{v.desc}</p>
                </Reveal>
              ))}
          </div>

          <h3 className="speaks-subheading">
            We Value Our <span className="accent">Employees</span>
          </h3>
          <div className="benefits-row">
            {employeeItems.map((item, i) => {
              const isActive = i === activeEmployeeIndex;
              return (
                <Reveal
                  as="div"
                  variant="up"
                  delay={i * 110}
                  key={item.title}
                  className={`benefit-card ${isActive ? 'is-active' : ''}`}
                  onMouseEnter={() => setActiveEmployeeIndex(i)}
                  onFocus={() => setActiveEmployeeIndex(i)}
                  onMouseLeave={() =>
                    setActiveEmployeeIndex((current) => (current === i ? null : current))
                  }
                  onBlur={() =>
                    setActiveEmployeeIndex((current) => (current === i ? null : current))
                  }
                  tabIndex={0}
                >
                  <div className="benefit-card-image">
                    <img src={valueImages[item.title]} alt={item.title} />
                  </div>
                  <div className="benefit-card-content">
                    <h4 className="benefit-card-title">{item.title}</h4>
                    <p className="benefit-card-hint">{valueHints[item.title]}</p>
                    <p className="benefit-card-desc">{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <div className="benefits-progress">
            <div className="benefits-progress-track">
              <div
                className="benefits-progress-fill"
                style={{
                  width:
                    activeEmployeeIndex === null
                      ? '0%'
                      : `${((activeEmployeeIndex + 1) / employeeItems.length) * 100}%`,
                }}
              />
            </div>
            <div className="benefits-nav">
              <button
                type="button"
                className="benefits-nav-hex"
                onClick={() =>
                  setActiveEmployeeIndex((i) => {
                    const current = i === null ? 0 : i;
                    return (current - 1 + employeeItems.length) % employeeItems.length;
                  })
                }
                aria-label="Previous benefit"
              >
                &#8249;
              </button>
              <button
                type="button"
                className="benefits-nav-hex"
                onClick={() =>
                  setActiveEmployeeIndex((i) => {
                    const current = i === null ? -1 : i;
                    return (current + 1) % employeeItems.length;
                  })
                }
                aria-label="Next benefit"
              >
                &#8250;
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Leadership;
